import { useCallback, useEffect, useRef, useState } from 'react';
import { useApp } from '../lib/data/store';
import { useNav } from '../lib/nav';
import { getPosition, type LatLng } from '../lib/geo';
import { CONSENT_VERSION, readableInk } from '../lib/ui/support';
import { supabase } from '../lib/supabaseClient';
import {
  CandidateChips,
  DispositionRow,
  IssueChips,
  MoreDispositions,
  PitchHelper,
  SupportButtons,
  Toast,
  VoiceNoteButton,
} from '../components/door/Controls';

interface FormState {
  support: number | null;
  candidates: string[];
  issues: string[];
  wantsSign: boolean;
  canVolunteer: boolean;
  note: string;
  multiUnit: boolean;
  doors: string;
  supporters: string;
}
const EMPTY: FormState = {
  support: null,
  candidates: [],
  issues: [],
  wantsSign: false,
  canVolunteer: false,
  note: '',
  multiUnit: false,
  doors: '',
  supporters: '',
};

export function AtTheDoor() {
  const app = useApp();
  const { currentDoor, openDoor, setCurrentDoor, setTab, goBack, canGoBack } = useNav();
  const [form, setForm] = useState<FormState>(EMPTY);
  const [addrInput, setAddrInput] = useState('');
  const [addrTouched, setAddrTouched] = useState(false);
  const [showMore, setShowMore] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [listChips, setListChips] = useState<{ nation_slug: string; name: string; colour: string }[]>([]);
  const geoRef = useRef<LatLng | null>(null);
  const touchStart = useRef<{ x: number; y: number } | null>(null);

  const hh = app.households.find((h) => h.id === currentDoor) ?? null;
  const hstate = hh ? app.stateFor(hh.id) : null;

  // Capture a position fix when a door opens (best-effort, non-blocking).
  useEffect(() => {
    setForm(EMPTY);
    setAddrInput('');
    setAddrTouched(false);
    setShowMore(false);
    setListChips([]);
    if (!currentDoor) return;
    void getPosition().then((p) => (geoRef.current = p));
    // which candidate lists this household is on (privacy-preserving RPC)
    if (navigator.onLine) {
      void supabase
        .rpc('household_candidates', { p_household_id: currentDoor })
        .then(({ data }) => setListChips((data ?? []) as typeof listChips));
    }
  }, [currentDoor]);

  // Pre-fill the address field from the GPS suggestion (unless the volunteer edited it).
  useEffect(() => {
    if (!addrTouched && hh?.address_resolved) setAddrInput(hh.address_resolved);
  }, [hh?.address_resolved, addrTouched]);

  // Listless flow: after saving a door, return to the map. The volunteer walks to
  // the next door and taps "Knock here" again — there is no preloaded next door.
  const advance = useCallback(() => {
    setCurrentDoor(null);
    setTab('map');
  }, [setCurrentDoor, setTab]);

  const saveSpoke = useCallback(async () => {
    if (!hh) return;
    // Don't create an empty contact (e.g. a stray swipe). Require at least one signal.
    const empty =
      form.support == null &&
      form.candidates.length === 0 &&
      form.issues.length === 0 &&
      !form.wantsSign &&
      !form.canVolunteer &&
      !form.note.trim() &&
      !form.multiUnit;
    if (empty) {
      setToast('Pick a support level, or use Not home / More');
      return;
    }
    if (form.multiUnit) {
      // Parse once and keep a deliberate 0 ("got in, found no supporters") — only
      // an empty/invalid field becomes null (unknown).
      const toCount = (s: string) => {
        const n = parseInt(s, 10);
        return Number.isNaN(n) ? null : n;
      };
      await app.setHouseholdMultiUnit(hh.id, {
        is_multi_unit: true,
        unit_count: toCount(form.doors),
        supporters_found: toCount(form.supporters),
      });
    }
    await app.recordContact({
      householdId: hh.id,
      support_level: form.support,
      candidate_slugs: form.candidates,
      issue_tags: form.issues,
      wants_sign: form.wantsSign,
      can_volunteer: form.canVolunteer,
      note: form.note.trim() || null,
      geo: geoRef.current,
    });
    setToast('Saved');
    advance();
  }, [hh, form, app, advance]);

  const saveDisposition = useCallback(
    async (d: string) => {
      if (!hh) return;
      if (d === 'do_not_contact') {
        await app.setHouseholdFlags(hh.id, { do_not_contact: true });
      }
      await app.recordContact({ householdId: hh.id, disposition: d, geo: geoRef.current });
      setToast(d === 'not_home' ? 'Not home — next door' : 'Saved');
      advance();
    },
    [hh, app, advance],
  );

  // dismiss toast
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 3500);
    return () => clearTimeout(t);
  }, [toast]);

  // swipe: left = save & next, right = previous
  const onTouchStart = (e: React.TouchEvent) => {
    touchStart.current = { x: e.touches[0]!.clientX, y: e.touches[0]!.clientY };
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    const s = touchStart.current;
    if (!s) return;
    const dx = e.changedTouches[0]!.clientX - s.x;
    const dy = e.changedTouches[0]!.clientY - s.y;
    if (Math.abs(dx) > 70 && Math.abs(dx) > Math.abs(dy) * 1.5) {
      if (dx < 0) void saveSpoke(); // swipe left → save & next
      else if (canGoBack) goBack(); // swipe right → previous door
    }
    touchStart.current = null;
  };

  // ---- empty state: no door open ----
  if (!hh) {
    return (
      <div className="fullcenter center">
        <h2>Ready to canvass</h2>
        <p className="muted">Walk to a door and tap Knock here. The app drops a pin and saves, even with no signal.</p>
        <button
          className="btn btn-primary btn-lg"
          onClick={async () => {
            const pos = await getPosition();
            if (!pos) {
              setToast('Could not get your location. Enable GPS.');
              return;
            }
            const id = await app.knockHere(pos);
            if (id) openDoor(id);
            else setToast('No district assigned — ask your coordinator.');
          }}
        >
          ➕ Knock here
        </button>
        {toast && <Toast message={toast} />}
      </div>
    );
  }

  const leanHint = hstate?.candidateLeans.length ? hstate.candidateLeans.map(app.nameForSlug).join(', ') : null;

  return (
    <div onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
      {/* 1. Header */}
      <div className="card" style={{ marginBottom: 0 }}>
        <div className="row spread">
          <div>
            {hh.address_omitted ? (
              <div style={{ fontWeight: 500, fontSize: 'calc(18px * var(--scale))' }}>(address suppressed — PIPA)</div>
            ) : hh.address ? (
              // real address (from the elector list / turf flow) — static
              <div style={{ fontWeight: 500, fontSize: 'calc(18px * var(--scale))' }}>{hh.address}</div>
            ) : hh.district_id ? (
              // listless GPS household — editable, auto-filled address entry
              <label className="field" style={{ marginBottom: 0 }}>
                <span>Address {hh.address_resolved ? '(auto-filled from GPS — edit if wrong)' : '(locating…)'}</span>
                <input
                  type="text"
                  value={addrInput}
                  placeholder="e.g. 545 Superior St"
                  onChange={(e) => {
                    setAddrInput(e.target.value);
                    setAddrTouched(true);
                  }}
                  onBlur={() => {
                    if (addrTouched && addrInput.trim() !== (hh.address_resolved ?? '')) {
                      void app.setHouseholdAddress(hh.id, addrInput);
                    }
                  }}
                />
              </label>
            ) : (
              <div style={{ fontWeight: 500, fontSize: 'calc(18px * var(--scale))' }}>New door</div>
            )}
            <div className="muted">
              {hh.elector_name ?? 'Resident'}{' '}
              {hh.on_elector_list && (
                <span className="badge ok" aria-label="on elector list, verified">
                  ✓ on list
                </span>
              )}
              {hh.do_not_contact && <span className="badge dnc">Do not contact</span>}
              {hh.flagged_unsafe && <span className="badge warn">⚠ flagged</span>}
            </div>
          </div>
        </div>
        {listChips.length > 0 && (
          <div className="chips" style={{ marginTop: 8 }}>
            {listChips.map((c) => (
              <span key={c.nation_slug} className="badge" style={{ background: c.colour, color: readableInk(c.colour) }}>
                {c.name.split(' ')[0]}
              </span>
            ))}
          </div>
        )}
        {hstate && hstate.attempts > 0 && (
          <div className="muted" style={{ marginTop: 6, fontSize: 'calc(12px * var(--scale))' }}>
            Attempt #{hstate.attempts + 1}
            {hstate.lastDisposition ? ` · last: ${hstate.lastDisposition.replace(/_/g, ' ')}` : ''}
          </div>
        )}
      </div>

      <div style={{ padding: '0 12px' }}>
        {/* 2. Disposition — Not home is 1 tap */}
        <div style={{ margin: '12px 0' }}>
          <DispositionRow onPick={saveDisposition} onMore={() => setShowMore((v) => !v)} />
          {showMore && (
            <MoreDispositions
              onPick={(d) => {
                setShowMore(false);
                void saveDisposition(d);
              }}
              onClose={() => setShowMore(false)}
            />
          )}
        </div>

        {/* 3. Pitch helper + consent */}
        <PitchHelper leanHint={leanHint} />

        {/* 4. Support — the only required field on Path A */}
        <h3 style={{ margin: '12px 0 6px', fontSize: 'calc(14px * var(--scale))' }} className="muted">
          Support level
        </h3>
        <SupportButtons value={form.support} onChange={(v) => setForm((f) => ({ ...f, support: v }))} />

        {/* 5. Candidate resonance */}
        <h3 style={{ margin: '16px 0 6px', fontSize: 'calc(14px * var(--scale))' }} className="muted">
          Which candidates resonated?
        </h3>
        <CandidateChips
          candidates={app.candidates}
          selected={form.candidates}
          onToggle={(slug) =>
            setForm((f) => ({
              ...f,
              candidates: f.candidates.includes(slug) ? f.candidates.filter((s) => s !== slug) : [...f.candidates, slug],
            }))
          }
        />

        {/* 6. Top issues */}
        <h3 style={{ margin: '16px 0 6px', fontSize: 'calc(14px * var(--scale))' }} className="muted">
          Top issues
        </h3>
        <IssueChips
          selected={form.issues}
          onToggle={(t) =>
            setForm((f) => ({ ...f, issues: f.issues.includes(t) ? f.issues.filter((s) => s !== t) : [...f.issues, t] }))
          }
        />

        {/* 7. Quick flags */}
        <div className="row wrap" style={{ margin: '16px 0', gap: 8 }}>
          <button
            type="button"
            className="chip"
            aria-pressed={form.wantsSign}
            onClick={() => setForm((f) => ({ ...f, wantsSign: !f.wantsSign }))}
          >
            🪧 Wants a sign <span className="check">✓</span>
          </button>
          <button
            type="button"
            className="chip"
            aria-pressed={form.canVolunteer}
            onClick={() => setForm((f) => ({ ...f, canVolunteer: !f.canVolunteer }))}
          >
            🙋 Can volunteer <span className="check">✓</span>
          </button>
        </div>
        {form.wantsSign && (
          <p className="muted" style={{ fontSize: 'calc(12px * var(--scale))', marginTop: -8 }}>
            📍 Location pinned for the sign-delivery list on save.
          </p>
        )}

        {/* 7b. Multi-unit building — log a door + supporter count, not one pin per unit */}
        <button
          type="button"
          className="chip"
          aria-pressed={form.multiUnit}
          style={{ margin: '8px 0' }}
          onClick={() => setForm((f) => ({ ...f, multiUnit: !f.multiUnit }))}
        >
          🏢 Multi-unit building <span className="check">✓</span>
        </button>
        {form.multiUnit && (
          <div className="row" style={{ gap: 8 }}>
            <label className="field grow" style={{ marginBottom: 0 }}>
              <span>Doors knocked</span>
              <input
                type="number"
                inputMode="numeric"
                min={0}
                value={form.doors}
                placeholder="e.g. 12"
                onChange={(e) => setForm((f) => ({ ...f, doors: e.target.value }))}
              />
            </label>
            <label className="field grow" style={{ marginBottom: 0 }}>
              <span>Supporters found</span>
              <input
                type="number"
                inputMode="numeric"
                min={0}
                value={form.supporters}
                placeholder="e.g. 4"
                onChange={(e) => setForm((f) => ({ ...f, supporters: e.target.value }))}
              />
            </label>
          </div>
        )}
        {form.multiUnit && (
          <p className="muted" style={{ fontSize: 'calc(11px * var(--scale))' }}>
            For secured towers, default is to skip — only log if you got in.
          </p>
        )}

        {/* 8. Note + voice */}
        <div className="row" style={{ gap: 8, margin: '8px 0' }}>
          <textarea
            className="grow"
            placeholder="Optional note…"
            value={form.note}
            onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
          />
        </div>
        <VoiceNoteButton onText={(t) => setForm((f) => ({ ...f, note: (f.note ? f.note + ' ' : '') + t }))} />

        {/* Safety / DNC quick flags */}
        <div className="row wrap" style={{ gap: 8, margin: '16px 0' }}>
          <button type="button" className="btn btn-danger" onClick={() => void saveDisposition('do_not_contact')}>
            🚫 Do not contact
          </button>
          {hh.flagged_unsafe ? (
            <span className="badge warn" style={{ alignSelf: 'center' }}>
              ⚠ Flagged unsafe — a coordinator can clear it
            </span>
          ) : (
            <button type="button" className="btn" onClick={() => app.setHouseholdFlags(hh.id, { flagged_unsafe: true })}>
              ⚠ Flag unsafe
            </button>
          )}
        </div>

        {/* 9. Consent stamp (automatic, visible) */}
        <p className="muted center" style={{ fontSize: 'calc(12px * var(--scale))' }}>
          ✓ Consent logged automatically · script {CONSENT_VERSION}
        </p>
      </div>

      {/* 10. Fixed bottom: Save & next */}
      <div className="actionbar">
        <button className="btn btn-primary btn-lg" onClick={() => void saveSpoke()}>
          Save &amp; next →
        </button>
      </div>

      {toast && (
        <Toast
          message={toast}
          actionLabel="Undo"
          onAction={async () => {
            const ok = await app.undoLast();
            setToast(ok ? 'Undone' : 'Already synced — can’t undo');
          }}
        />
      )}
    </div>
  );
}
